import { ChevronsUpDown } from 'lucide-react'
import { OrderbookTableContentBaseProps, formatDateDDMMYYYY } from './OrderbookTableContentBase'
import type { OrderbookItem } from '@/types/otc'
import { formatCompactNumber, getOrderTypeVariant } from '@/utils'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { GatedValue } from '@/components/gate'

interface OrderbookTableContentDesktopProps extends OrderbookTableContentBaseProps {}

type HeaderColumn = {
  key: string
  label: string
  width: string
  sortable?: boolean
}

const COLUMNS: HeaderColumn[] = [
  { key: 'project_name', label: 'Project', width: '23.6041%' },
  { key: 'type', label: 'Type', width: '11.4213%' },
  { key: 'deal_type', label: 'Asset Type', width: '11.4213%' },
  { key: 'offered_amount', label: 'Amount', width: '13.3249%', sortable: true },
  { key: 'offered_fully_diluted_value', label: 'FDV', width: '13.3249%' },
  { key: 'round', label: 'Round', width: '12.6904%' },
  { key: 'date', label: 'Data', width: '14.2131%', sortable: true },
]

/**
 * 🔴 Is this cell's number absent because the caller is not entitled to it?
 *
 * The caller's TIER is the authority — a gated session gets its rows from
 * `GET /orderbook/public`, which does not read the financial columns at all,
 * so there is no value in the DOM, in the React tree or in the network
 * response to reveal. `financials_hidden` on the row is the backstop, so a
 * public row is drawn as locked even if it somehow reached an ungated table.
 *
 * The row's own `undefined` deliberately does NOT count on its own: at `full`
 * tier a genuinely missing amount is missing DATA, and `formatCompactNumber`
 * already renders it as an em dash. Drawing a lock there would tell a verified
 * user they are not allowed to see something they are perfectly entitled to.
 */
const isGated = (item: OrderbookItem, financialsHidden: boolean): boolean =>
  financialsHidden || item.financials_hidden === true

const HeaderCell = ({
  column,
  sortColumn,
  sortDirection,
  onSort,
  financialsHidden,
}: {
  column: HeaderColumn
  sortColumn?: string
  sortDirection?: 'asc' | 'desc'
  onSort?: (columnKey: string) => void
  financialsHidden?: boolean
}) => {
  if (column.key === 'project_name') {
    return (
      <th className="h-[52px] px-4 text-left">
        <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
          {column.label}
        </span>
      </th>
    )
  }

  // 🔴 Sorting by Amount is disabled for a gated listing, and not only because
  // the public endpoint answers 400 for `sort_by=OFFERED_AMOUNT`. Sorting on a
  // hidden column IS a read of it: paginate a sorted list and a binary search
  // recovers individual values. The backend refuses it for that reason; the UI
  // must not offer a control that would be rejected, or imply the ordering is
  // available.
  const isSortable = Boolean(column.sortable) && !(financialsHidden && column.key === 'offered_amount')
  const isActive = sortColumn === column.key && sortDirection

  return (
    <th className="h-[52px] px-4 text-left">
      <button
        type="button"
        className={cn(
          'inline-flex h-[36px] items-center gap-2 rounded-md px-4',
          isSortable ? 'cursor-pointer' : 'cursor-default'
        )}
        onClick={isSortable ? () => onSort?.(column.key) : undefined}
      >
        <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
          {column.label}
        </span>
        <ChevronsUpDown
          className={cn(
            'h-4 w-4',
            isActive
              ? 'text-[rgba(45,41,35,0.9)] dark:text-[rgba(253,253,253,0.9)]'
              : 'text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]'
          )}
        />
      </button>
    </th>
  )
}

const renderCell = (item: OrderbookItem, key: string, financialsHidden: boolean) => {
  if (key === 'project_name') {
    return (
      <div className="flex items-center gap-[10px]">
        {item.logo ? (
          <img
            src={item.logo}
            alt={item.project_name}
            className="h-[26px] w-[26px] rounded-full object-cover"
            onError={(e) => {
              e.currentTarget.src = '/placeholder-logo.png'
            }}
          />
        ) : (
          <div className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-[#f5f5f5] dark:bg-[rgba(253,253,253,0.12)]">
            <span className="text-xs font-semibold text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.72)]">
              {item.project_name?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
        )}
        <span className="truncate font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
          {item.project_name}
        </span>
      </div>
    )
  }

  if (key === 'type') {
    return (
      <Badge variant={getOrderTypeVariant(item.type)}>
        {item.type}
      </Badge>
    )
  }

  if (key === 'deal_type') {
    return (
      <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {item.deal_type}
      </span>
    )
  }

  if (key === 'offered_amount') {
    return isGated(item, financialsHidden) ? (
      <GatedValue label="Offered amount" widthCh={5} />
    ) : (
      <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        ${formatCompactNumber(item.offered_amount)}
      </span>
    )
  }

  if (key === 'offered_fully_diluted_value') {
    return isGated(item, financialsHidden) ? (
      <GatedValue label="Fully diluted valuation" widthCh={5} />
    ) : (
      <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        ${formatCompactNumber(item.offered_fully_diluted_value)}
      </span>
    )
  }

  if (key === 'round') {
    return (
      <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {item.round || '－'}
      </span>
    )
  }

  if (key === 'date') {
    return (
      <span className="font-plus-jakarta text-[14px] font-normal leading-[20px] text-[#2d2923] dark:text-[#fdfdfd]">
        {formatDateDDMMYYYY(item.date)}
      </span>
    )
  }

  return null
}

export const OrderbookTableContentDesktop = ({
  data,
  isLoading = false,
  onRowClick,
  emptyMessage = 'No orders available',
  sortColumn,
  sortDirection,
  onSort,
  financialsHidden = false,
}: OrderbookTableContentDesktopProps) => {
  // 🔴 Driven by the caller's ENTITLEMENT, not by the rows. Deriving it from
  // the data would leave the Amount sort clickable on the first paint (the
  // rows have not arrived yet) and on an empty result — a control that cannot
  // work, offering an ordering that is not available.

  return (
    <div className="w-full overflow-hidden bg-[rgba(255,255,255,0.32)] dark:bg-[#0a0a0a]">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          {COLUMNS.map((column) => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead>
          <tr className="border-b border-[#e5e5e5] dark:border-[#0a0a0a]">
            {COLUMNS.map((column) => (
              <HeaderCell
                key={column.key}
                column={column}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={onSort}
                financialsHidden={financialsHidden}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={COLUMNS.length} className="h-[120px] text-center text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                Loading...
              </td>
            </tr>
          )}

          {!isLoading && data.length === 0 && (
            <tr>
              <td colSpan={COLUMNS.length} className="h-[120px] text-center text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
                {emptyMessage}
              </td>
            </tr>
          )}

          {!isLoading && data.map((item, index) => (
            <tr
              key={item.order_id}
              className={cn(
                'h-[68px] border-b border-[#e5e5e5] dark:border-[#0a0a0a] transition-colors',
                index % 2 === 0 && 'bg-[rgba(255,255,255,0.28)] dark:bg-[rgba(28,28,28,0.8)]',
                onRowClick && 'cursor-pointer hover:bg-[rgba(255,255,255,0.45)] dark:hover:bg-[rgba(28,28,28,0.92)]'
              )}
              onClick={() => onRowClick?.(item)}
            >
              {COLUMNS.map((column) => (
                <td key={`${item.order_id}-${column.key}`} className="px-4 align-middle">
                  {renderCell(item, column.key, financialsHidden)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
